/**
 * Medicines: the catalogue, and one medicine's units, batches and shelves.
 *
 * Every quantity the API sends is in base units; this screen only names them
 * in the largest units that divide them. A unit's size is defined once, here,
 * and the server converts every count against it — the till never says how
 * many tablets a strip holds.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, PackagePlus, Pill, Plus, SlidersHorizontal, Trash2 } from 'lucide-react';
import {
  RX_CLASSES,
  RX_CLASS_LABELS,
  STORAGE_REQUIREMENTS,
  STORAGE_REQUIREMENT_LABELS,
} from '@cenox/shared';
import { useAuth } from '../../lib/auth';
import { useWorkingLocation } from '../../lib/locationContext';
import { money } from '../../lib/format';
import { Badge, Button, Card, CardHeader, ConfirmDialog, EmptyState, ErrorState, Input, Modal, Select, Spinner } from '../../components/ui';
import { PageHeader } from '../../layouts/AppShell';
import { AdjustmentModal } from './AdjustmentsPage';
import { CatalogueList } from './CatalogueReview';
import { OpeningStockCard } from './OpeningStockCard';
import { usePharmacy, usePharmacyWrite, type BatchRow, type ListOf, type MedicineListItem, type UnitView } from './data';
import { ErrorLine, ExpiryBadge, RxBadge, SectionTabs, Table, Td, describeQuantity, errorText } from './parts';

// ── Shared by the Medicines & Stock screens ────────────────────────────────

export function StockTabs(): ReactNode {
  const { can } = useAuth();
  return (
    <SectionTabs
      tabs={[
        { to: '/pharmacy/stock', label: 'Medicines' },
        { to: '/pharmacy/stock/batches', label: 'Batches & expiry' },
        { to: '/pharmacy/stock/adjustments', label: 'Adjustments & quarantine' },
        { to: '/pharmacy/stock/counts', label: 'Stock counts', end: false },
        ...(can('recalls:manage') ? [{ to: '/pharmacy/stock/recalls', label: 'Recalls', end: false }] : []),
        { to: '/pharmacy/stock/storage', label: 'Storage' },
      ]}
    />
  );
}

/**
 * The location a write happens at: the one being worked in, else the default.
 * When neither settles it, the caller shows `LocationPicker`.
 */
export function useActionBranch(): {
  branchId: string;
  setBranchId: (id: string) => void;
  options: Array<{ value: string; label: string }>;
  ambiguous: boolean;
} {
  const { branchId, defaultBranchId, locations } = useWorkingLocation();
  const [chosen, setChosen] = useState<string | null>(null);
  const resolved = chosen ?? branchId ?? defaultBranchId ?? locations[0]?.id ?? '';
  return {
    branchId: resolved,
    setBranchId: setChosen,
    options: locations.map((l) => ({ value: l.id, label: l.name })),
    ambiguous: !branchId && locations.length > 1,
  };
}

export function LocationPicker({
  value,
  onChange,
  options,
  label = 'Location',
}: {
  value: string;
  onChange: (id: string) => void;
  options: Array<{ value: string; label: string }>;
  label?: string;
}): ReactNode {
  if (options.length <= 1) return null;
  return <Select label={label} value={value} onChange={(e) => onChange(e.target.value)} options={options} />;
}

export function Checkbox({
  label,
  checked,
  onChange,
  hint,
  disabled,
}: {
  label: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  hint?: string;
  disabled?: boolean;
}): ReactNode {
  return (
    <label className="flex items-start gap-2 text-sm text-slate-700">
      <input
        type="checkbox"
        className="mt-0.5 size-4 rounded border-slate-300 text-brand-600"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>
        {label}
        {hint && <span className="block text-xs text-slate-500">{hint}</span>}
      </span>
    </label>
  );
}

/** Pick a medicine by typing; returns its id and name. */
export function ProductPicker({
  value,
  onChange,
  label = 'Medicine or product',
}: {
  value: { id: string; name: string } | null;
  onChange: (next: { id: string; name: string } | null) => void;
  label?: string;
}): ReactNode {
  const [search, setSearch] = useState('');
  const found = usePharmacy<ListOf<MedicineListItem>>('/medicines', { search, limit: 8 }, search.trim().length >= 2 && !value);
  const pick = (m: { id: string; name: string }) => {
    setSearch('');
    onChange({ id: m.id, name: m.name });
  };
  if (value) {
    return (
      <div>
        <p className="label-base">{label}</p>
        <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
          <span className="font-medium text-slate-900">{value.name}</span>
          <button type="button" className="text-brand-700 hover:underline" onClick={() => onChange(null)}>
            Change
          </button>
        </div>
      </div>
    );
  }
  return (
    <div>
      <Input
        label={label}
        placeholder="Type a name, generic, SKU or barcode"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        onKeyDown={(e) => {
          // A scanner types the code and presses Enter: one match is that product.
          if (e.key !== 'Enter') return;
          e.preventDefault();
          const hits = found.data?.data ?? [];
          const code = search.trim().toUpperCase();
          const exact = hits.filter((m) => m.units.some((u) => (u.barcode ?? '').toUpperCase() === code));
          const only = exact.length === 1 ? exact[0] : hits.length === 1 ? hits[0] : undefined;
          if (only) pick(only);
        }}
      />
      {found.data && search.trim().length >= 2 && (
        <ul className="mt-1 max-h-48 overflow-y-auto rounded-lg border border-slate-200 bg-white text-sm shadow-sm">
          {found.data.data.length === 0 && <li className="px-3 py-2 text-slate-500">Nothing matches.</li>}
          {found.data.data.map((m) => (
            <li key={m.id}>
              <button
                type="button"
                className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left hover:bg-slate-50"
                onClick={() => pick(m)}
              >
                <span>
                  {m.name}
                  {m.strength && <span className="text-slate-500"> · {m.strength}</span>}
                </span>
                <RxBadge rxClass={m.rxClass} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const isWhole = (v: string, min: number) => /^\d+$/.test(v.trim()) && Number(v) >= min;

// ── Catalogue ──────────────────────────────────────────────────────────────

export function MedicinesPage(): ReactNode {
  const { can } = useAuth();
  const { branchId } = useWorkingLocation();
  const [adding, setAdding] = useState(false);

  return (
    <div>
      <PageHeader
        title="Medicines & products"
        description="Every product on file — medicines, cosmetics, supplements and provisions — and what each one is"
        action={
          <div className="flex flex-wrap gap-2">
            {(can('pharmacy:stock') || can('pharmacy:purchasing')) && (
              <Link to="/pharmacy/stock/entry">
                <Button variant="secondary" icon={<PackagePlus className="size-4" />}>Add stock</Button>
              </Link>
            )}
            {can('pharmacy:catalogue') && (
              <Button icon={<Plus className="size-4" />} onClick={() => setAdding(true)}>
                Add medicine
              </Button>
            )}
          </div>
        }
      />
      <StockTabs />
      <CatalogueList branchId={branchId} />
      <AddMedicineModal open={adding} onClose={() => setAdding(false)} />
    </div>
  );
}

interface UnitDraft {
  name: string;
  baseQuantity: string;
  barcode: string;
  price: string;
}

function AddMedicineModal({ open, onClose }: { open: boolean; onClose: () => void }): ReactNode {
  const { can } = useAuth();
  // Deciding that an item is a medicine, and its class, is a pharmacist's call.
  const mayClassify = can('pharmacy:classify');
  const create = usePharmacyWrite<Record<string, unknown>>('post', '/medicines');
  const [f, setF] = useState({
    name: '',
    sku: '',
    isMedicine: mayClassify,
    tracksBatches: true,
    genericName: '',
    brandName: '',
    strength: '',
    dosageForm: '',
    manufacturer: '',
    registrationNumber: '',
    // Neither is preselected for a medicine: the pharmacist states both.
    rxClass: '',
    storageRequirement: '',
    baseUnitName: 'tablet',
    baseUnitBarcode: '',
    baseUnitPrice: '',
    baseUnitSellable: true,
  });
  const [units, setUnits] = useState<UnitDraft[]>([
    { name: 'strip', baseQuantity: '10', barcode: '', price: '' },
    { name: 'pack', baseQuantity: '100', barcode: '', price: '' },
  ]);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));

  const unitErrors = units.map((u) =>
    !u.name.trim() ? 'Name the unit' : !isWhole(u.baseQuantity, 2) ? `Whole number of ${f.baseUnitName || 'base units'}, 2 or more` : null,
  );
  const preview = useMemo(() => {
    const valid = units.filter((u, i) => !unitErrors[i]).map((u) => ({ name: u.name.trim(), size: Number(u.baseQuantity) }));
    if (!valid.length) return null;
    const sorted = [...valid].sort((a, b) => b.size - a.size);
    const top = sorted[0]!;
    const parts = [`1 ${top.name}`];
    for (const u of sorted.slice(1)) {
      const n = top.size / u.size;
      parts.push(Number.isInteger(n) ? `${n} ${u.name}s` : `${n.toFixed(2)} ${u.name}s (not a whole number)`);
    }
    parts.push(`${top.size} ${f.baseUnitName || 'base units'}`);
    return parts.join(' = ');
  }, [units, unitErrors, f.baseUnitName]);

  const ready =
    f.name.trim() &&
    f.baseUnitName.trim() &&
    unitErrors.every((e) => !e) &&
    (!f.isMedicine || (f.rxClass !== '' && f.storageRequirement !== ''));

  function submit() {
    create.mutate(
      {
        name: f.name.trim(),
        sku: f.sku.trim() || null,
        isMedicine: f.isMedicine,
        tracksBatches: f.isMedicine ? true : f.tracksBatches,
        genericName: f.genericName.trim() || null,
        brandName: f.brandName.trim() || null,
        strength: f.strength.trim() || null,
        dosageForm: f.dosageForm.trim() || null,
        manufacturer: f.manufacturer.trim() || null,
        registrationNumber: f.registrationNumber.trim() || null,
        ...(f.isMedicine ? { rxClass: f.rxClass, storageRequirement: f.storageRequirement } : {}),
        ...(!f.isMedicine && f.storageRequirement ? { storageRequirement: f.storageRequirement } : {}),
        baseUnitName: f.baseUnitName.trim(),
        baseUnitBarcode: f.baseUnitBarcode.trim() || null,
        baseUnitPrice: f.baseUnitPrice.trim() || null,
        baseUnitSellable: f.baseUnitSellable,
        units: units.map((u) => ({
          name: u.name.trim(),
          baseQuantity: Number(u.baseQuantity),
          barcode: u.barcode.trim() || null,
          price: u.price.trim() || null,
          sellable: true,
          purchasable: true,
        })),
      },
      { onSuccess: onClose },
    );
  }

  return (
    <Modal open={open} onClose={onClose} title="Add medicine or product" size="lg">
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <Input label="Name" required value={f.name} onChange={(e) => set('name', e.target.value)} />
          <Input label="SKU" value={f.sku} onChange={(e) => set('sku', e.target.value)} />
        </div>
        <div className="space-y-2 rounded-lg border border-slate-200 p-3">
          {mayClassify ? (
            <Checkbox
              label="This is a medicine"
              hint="Medicines are always tracked by batch and expiry. Untick for cosmetics and ordinary retail."
              checked={f.isMedicine}
              onChange={(v) => set('isMedicine', v)}
            />
          ) : (
            <p className="text-sm text-slate-600">
              You can add cosmetics and ordinary retail items. Adding a medicine, and choosing its class, is for a pharmacist.
            </p>
          )}
          {!f.isMedicine && (
            <Checkbox
              label="Track by batch and expiry"
              hint="Leave on for cosmetics that expire; off for items with no expiry."
              checked={f.tracksBatches}
              onChange={(v) => set('tracksBatches', v)}
            />
          )}
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <Input label="Generic name" value={f.genericName} onChange={(e) => set('genericName', e.target.value)} />
          <Input label="Brand" value={f.brandName} onChange={(e) => set('brandName', e.target.value)} />
          <Input label="Strength" placeholder="500 mg" value={f.strength} onChange={(e) => set('strength', e.target.value)} />
          <Input label="Dosage form" placeholder="tablet, syrup, cream" value={f.dosageForm} onChange={(e) => set('dosageForm', e.target.value)} />
          <Input label="Manufacturer" value={f.manufacturer} onChange={(e) => set('manufacturer', e.target.value)} />
          <Input label="Registration (NAFDAC) no." value={f.registrationNumber} onChange={(e) => set('registrationNumber', e.target.value)} />
          {f.isMedicine && (
            <Select
              label="Classification"
              value={f.rxClass}
              onChange={(e) => set('rxClass', e.target.value)}
              options={[{ value: '', label: 'Choose the class' }, ...RX_CLASSES.map((r) => ({ value: r, label: RX_CLASS_LABELS[r] }))]}
            />
          )}
          <Select
            label="Storage"
            value={f.storageRequirement}
            onChange={(e) => set('storageRequirement', e.target.value)}
            options={[
              { value: '', label: f.isMedicine ? 'Choose the storage' : 'Room temperature (not stated)' },
              ...STORAGE_REQUIREMENTS.map((s) => ({ value: s, label: STORAGE_REQUIREMENT_LABELS[s] })),
            ]}
          />
        </div>

        <div className="rounded-lg border border-slate-200 p-3">
          <p className="mb-2 text-sm font-semibold text-slate-900">Base unit — the smallest thing ever handed over</p>
          <div className="grid gap-3 sm:grid-cols-3">
            <Input label="Name" required value={f.baseUnitName} onChange={(e) => set('baseUnitName', e.target.value)} />
            <Input label="Barcode" value={f.baseUnitBarcode} onChange={(e) => set('baseUnitBarcode', e.target.value)} />
            <Input label="Price" inputMode="decimal" value={f.baseUnitPrice} onChange={(e) => set('baseUnitPrice', e.target.value)} />
          </div>
          <div className="mt-2">
            <Checkbox label={`Sold by the single ${f.baseUnitName || 'unit'}`} checked={f.baseUnitSellable} onChange={(v) => set('baseUnitSellable', v)} />
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 p-3">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-sm font-semibold text-slate-900">Larger units</p>
            <Button size="sm" variant="secondary" icon={<Plus className="size-4" />} onClick={() => setUnits((u) => [...u, { name: '', baseQuantity: '', barcode: '', price: '' }])}>
              Add unit
            </Button>
          </div>
          <div className="space-y-3">
            {units.map((u, i) => (
              <div key={i} className="grid grid-cols-2 gap-2 sm:grid-cols-[1fr_1fr_1fr_1fr_auto] sm:items-end">
                <Input label="Unit" value={u.name} onChange={(e) => setUnits((all) => all.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} />
                <Input
                  label={`${f.baseUnitName || 'Base units'} in it`}
                  inputMode="numeric"
                  value={u.baseQuantity}
                  error={u.name || u.baseQuantity ? (unitErrors[i] ?? undefined) : undefined}
                  onChange={(e) => setUnits((all) => all.map((x, j) => (j === i ? { ...x, baseQuantity: e.target.value } : x)))}
                />
                <Input label="Barcode" value={u.barcode} onChange={(e) => setUnits((all) => all.map((x, j) => (j === i ? { ...x, barcode: e.target.value } : x)))} />
                <Input label="Price" inputMode="decimal" value={u.price} onChange={(e) => setUnits((all) => all.map((x, j) => (j === i ? { ...x, price: e.target.value } : x)))} />
                <Button variant="ghost" size="sm" aria-label="Remove unit" icon={<Trash2 className="size-4" />} onClick={() => setUnits((all) => all.filter((_, j) => j !== i))} />
              </div>
            ))}
          </div>
          {preview && <p className="mt-3 rounded-md bg-brand-50 px-3 py-2 text-sm text-brand-800">{preview}</p>}
        </div>

        <ErrorLine error={create.error} />
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!ready} loading={create.isPending} onClick={submit}>
            Save
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ── One medicine ───────────────────────────────────────────────────────────

interface MedicineView {
  id: string;
  name: string;
  sku: string | null;
  isMedicine: boolean;
  genericName: string | null;
  brandName: string | null;
  strength: string | null;
  dosageForm: string | null;
  manufacturer: string | null;
  registrationNumber: string | null;
  rxClass: string;
  storageRequirement: string;
  tracksBatches: boolean;
  baseUnitName: string;
  units: UnitView[];
  onHand: number;
  awaitingVerification?: number;
  locations: Array<{ branchId: string; shelfId: string | null; shelfCode: string | null; reorderLevel: number | null }>;
}

export function MedicineDetailPage(): ReactNode {
  const { id = '' } = useParams();
  const { can, business } = useAuth();
  const { locations } = useWorkingLocation();
  const c = business?.currencyCode ?? 'NGN';
  const med = usePharmacy<MedicineView>(`/medicines/${id}`);
  const batches = usePharmacy<ListOf<BatchRow>>('/batches', { productId: id });
  const prices = usePharmacy<ListOf<{ kind: string; price: string; unit_name: string | null; supplier_name: string | null; source: string; effective_at: string }>>(`/medicines/${id}/prices`);
  const [editing, setEditing] = useState(false);
  const [adjusting, setAdjusting] = useState(false);
  const [unitEdit, setUnitEdit] = useState<Partial<UnitView> | null>(null);
  const [shelfFor, setShelfFor] = useState<string | null>(null);
  const [retiring, setRetiring] = useState<UnitView | null>(null);
  const [correcting, setCorrecting] = useState<BatchRow | null>(null);
  const retire = usePharmacyWrite<{ unitId: string }>('del', (b) => `/medicines/${id}/units/${b.unitId}`);

  if (med.isError) return <ErrorState message="Could not load this medicine." onRetry={() => void med.refetch()} />;
  if (!med.data) return <Spinner />;
  const m = med.data;
  const branchName = (bid: string) => locations.find((l) => l.id === bid)?.name ?? 'Location';

  return (
    <div className="space-y-5">
      <Link to="/pharmacy/stock" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900">
        <ArrowLeft className="size-4" aria-hidden /> Medicines
      </Link>
      <PageHeader
        title={m.name}
        description={[m.genericName, m.strength, m.dosageForm, m.manufacturer].filter(Boolean).join(' · ') || undefined}
        action={
          <div className="flex flex-wrap gap-2">
            {(can('pharmacy:stock') || can('pharmacy:purchasing')) && (
              <Link to={`/pharmacy/stock/entry?product=${m.id}`}>
                <Button icon={<PackagePlus className="size-4" />}>Add stock</Button>
              </Link>
            )}
            {can('pharmacy:stock') && (
              <Button variant="secondary" icon={<SlidersHorizontal className="size-4" />} onClick={() => setAdjusting(true)}>
                Adjust stock
              </Button>
            )}
            {can('pharmacy:catalogue') && <Button variant="secondary" onClick={() => setEditing(true)}>Edit details</Button>}
          </div>
        }
      />
      <div className="flex flex-wrap items-center gap-2">
        <RxBadge rxClass={m.rxClass} />
        <Badge>{STORAGE_REQUIREMENT_LABELS[m.storageRequirement as keyof typeof STORAGE_REQUIREMENT_LABELS] ?? m.storageRequirement}</Badge>
        {m.registrationNumber && <Badge tone="info">Reg. {m.registrationNumber}</Badge>}
        {!m.tracksBatches && <Badge tone="warning">Not batch-tracked</Badge>}
        {(m.awaitingVerification ?? 0) > 0 ? (
          <>
            <span className="text-sm text-slate-600">
              Recorded stock everywhere: {describeQuantity(m.onHand + m.awaitingVerification!, m.units)} ({describeQuantity(m.onHand, m.units)} with batches)
            </span>
            <Badge tone="warning">Counted stock — batch/expiry pending: {describeQuantity(m.awaitingVerification!, m.units)} · not for sale</Badge>
          </>
        ) : (
          <span className="text-sm text-slate-600">On hand everywhere: {describeQuantity(m.onHand, m.units)}</span>
        )}
      </div>

      <ClassificationHistory productId={m.id} />

      <Card padded={false}>
        <div className="p-4 sm:p-5">
          <CardHeader
            title="Selling units"
            subtitle="A unit that has been sold or received keeps its size; add a new unit instead of resizing it."
            action={can('pharmacy:catalogue') ? <Button size="sm" variant="secondary" icon={<Plus className="size-4" />} onClick={() => setUnitEdit({})}>Add unit</Button> : undefined}
          />
        </div>
        <Table head={['Unit', 'Holds', 'Barcode', 'Price', 'Sold', '']}>
          {m.units.map((u) => (
            <tr key={u.id}>
              <Td className="font-medium">{u.name}{u.isBase && <span className="ml-1 text-xs text-slate-500">(base)</span>}</Td>
              <Td className="tabular-nums">{u.baseQuantity} {m.baseUnitName}</Td>
              <Td className="font-mono text-xs">{u.barcode ?? '—'}</Td>
              <Td className="tabular-nums">{u.price ? money(u.price, c) : '—'}</Td>
              <Td>{u.sellable ? 'Yes' : 'No'}</Td>
              <Td className="text-right">
                {can('pharmacy:catalogue') && (
                  <div className="flex justify-end gap-1">
                    <Button size="sm" variant="ghost" onClick={() => setUnitEdit(u)}>Edit</Button>
                    {!u.isBase && (
                      <Button size="sm" variant="ghost" onClick={() => { retire.reset(); setRetiring(u); }}>Retire</Button>
                    )}
                  </div>
                )}
              </Td>
            </tr>
          ))}
        </Table>
      </Card>

      <Card padded={false}>
        <div className="p-4 sm:p-5">
          <CardHeader title="Batches by location" subtitle="Earliest expiry is sold first" />
        </div>
        {!batches.data ? (
          <Spinner />
        ) : batches.data.data.length === 0 ? (
          <p className="px-5 pb-6 text-sm text-slate-500">No stock held.</p>
        ) : (
          <Table head={['Batch', 'Location', 'Expiry', 'Made', 'Received', 'On hand', 'Status', 'Value at cost', '']} minWidth={860}>
            {batches.data.data.map((b) => (
              <tr key={`${b.batchId}-${b.branchId}`}>
                <Td className="font-mono text-xs">{b.batchNumber}</Td>
                <Td>{branchName(b.branchId)}</Td>
                <Td><ExpiryBadge expiryDate={b.expiryDate} expired={b.expired} nearExpiry={b.nearExpiry} /></Td>
                <Td className="tabular-nums">{b.manufacturedOn ?? '—'}</Td>
                <Td className="tabular-nums">{b.receivedOn}</Td>
                <Td className="tabular-nums">{describeQuantity(b.onHand, m.units)}</Td>
                <Td>
                  {b.awaitingCheck ? (
                    <Badge tone="warning">awaiting pharmacist check</Badge>
                  ) : b.held ? (
                    b.holdKinds.map((k) => <Badge key={k} tone={k === 'recall' ? 'danger' : 'warning'}>{k}</Badge>)
                  ) : b.expired ? (
                    <Badge tone="danger">expired</Badge>
                  ) : (
                    <Badge tone="success">saleable</Badge>
                  )}
                </Td>
                <Td className="tabular-nums">{money(b.value, c)}</Td>
                <Td className="text-right">
                  {can('pharmacy:stock_approve') && (
                    <Button size="sm" variant="ghost" onClick={() => setCorrecting(b)}>Correct</Button>
                  )}
                </Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      <Card padded={false}>
        <div className="p-4 sm:p-5">
          <CardHeader title="Shelves and reorder levels" subtitle={`Reorder levels are in ${m.baseUnitName}s; at or below one, the item is flagged as low`} />
        </div>
        <Table head={['Location', 'Shelf', 'Reorder at', '']}>
          {locations.map((l) => {
            const at = m.locations.find((x) => x.branchId === l.id);
            return (
              <tr key={l.id}>
                <Td>{l.name}</Td>
                <Td>{at?.shelfCode ?? '—'}</Td>
                <Td className="tabular-nums">{at?.reorderLevel ?? '—'}</Td>
                <Td className="text-right">
                  {can('pharmacy:catalogue') && <Button size="sm" variant="ghost" onClick={() => setShelfFor(l.id)}>Set</Button>}
                </Td>
              </tr>
            );
          })}
        </Table>
      </Card>

      <Card padded={false}>
        <div className="p-4 sm:p-5">
          <CardHeader title="Price history" subtitle="Purchase prices from receipts; selling prices from the catalogue" />
        </div>
        {!prices.data ? (
          <Spinner />
        ) : prices.data.data.length === 0 ? (
          <p className="px-5 pb-6 text-sm text-slate-500">No prices recorded yet.</p>
        ) : (
          <Table head={['When', 'Kind', 'Unit', 'Price', 'Supplier', 'From']}>
            {prices.data.data.map((p, i) => (
              <tr key={i}>
                <Td className="tabular-nums">{p.effective_at.slice(0, 10)}</Td>
                <Td><Badge tone={p.kind === 'purchase' ? 'info' : 'neutral'}>{p.kind}</Badge></Td>
                <Td>{p.unit_name ?? '—'}</Td>
                <Td className="tabular-nums">{money(p.price, c)}</Td>
                <Td>{p.supplier_name ?? '—'}</Td>
                <Td>{p.source}</Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      <OpeningStockCard productId={m.id} unitName={m.baseUnitName} />
      {editing && <EditMedicineModal med={m} onClose={() => setEditing(false)} />}
      {adjusting && <AdjustmentModal preset={{ product: { id: m.id, name: m.name } }} onClose={() => setAdjusting(false)} />}
      {correcting && <CorrectBatchModal batch={correcting} onClose={() => setCorrecting(null)} />}
      <ConfirmDialog
        open={retiring !== null}
        onClose={() => setRetiring(null)}
        onConfirm={() => retiring && retire.mutate({ unitId: retiring.id }, { onSuccess: () => setRetiring(null) })}
        title={`Retire the ${retiring?.name ?? 'unit'}?`}
        description={
          <p>
            Past sales and receipts keep this unit exactly as recorded. From now on it can no longer be sold, bought or
            scanned. Stock is counted in {m.baseUnitName}s and is not affected.
          </p>
        }
        confirmLabel="Retire unit"
        loading={retire.isPending}
        error={errorText(retire.error)}
      />
      {unitEdit && <UnitModal productId={m.id} baseName={m.baseUnitName} unit={unitEdit} onClose={() => setUnitEdit(null)} />}
      {shelfFor && (
        <ShelfModal
          productId={m.id}
          branchId={shelfFor}
          current={m.locations.find((x) => x.branchId === shelfFor) ?? null}
          onClose={() => setShelfFor(null)}
        />
      )}
    </div>
  );
}

function EditMedicineModal({ med, onClose }: { med: MedicineView; onClose: () => void }): ReactNode {
  const { can } = useAuth();
  const mayClassify = can('pharmacy:classify');
  const save = usePharmacyWrite<Record<string, unknown>>('patch', `/medicines/${med.id}`);
  const [reason, setReason] = useState('');
  const [f, setF] = useState({
    name: med.name,
    sku: med.sku ?? '',
    genericName: med.genericName ?? '',
    brandName: med.brandName ?? '',
    strength: med.strength ?? '',
    dosageForm: med.dosageForm ?? '',
    manufacturer: med.manufacturer ?? '',
    registrationNumber: med.registrationNumber ?? '',
    rxClass: med.rxClass,
    storageRequirement: med.storageRequirement,
  });
  const set = (k: keyof typeof f, v: string) => setF((x) => ({ ...x, [k]: v }));
  const nul = (v: string) => v.trim() || null;
  return (
    <Modal open onClose={onClose} title="Edit details" size="lg">
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <Input label="Name" value={f.name} onChange={(e) => set('name', e.target.value)} />
          <Input label="SKU" value={f.sku} onChange={(e) => set('sku', e.target.value)} />
          <Input label="Generic name" value={f.genericName} onChange={(e) => set('genericName', e.target.value)} />
          <Input label="Brand" value={f.brandName} onChange={(e) => set('brandName', e.target.value)} />
          <Input label="Strength" value={f.strength} onChange={(e) => set('strength', e.target.value)} />
          <Input label="Dosage form" value={f.dosageForm} onChange={(e) => set('dosageForm', e.target.value)} />
          <Input label="Manufacturer" value={f.manufacturer} onChange={(e) => set('manufacturer', e.target.value)} />
          <Input label="Registration (NAFDAC) no." value={f.registrationNumber} onChange={(e) => set('registrationNumber', e.target.value)} />
          <Select
            label="Classification"
            value={f.rxClass}
            disabled={!mayClassify}
            {...(mayClassify ? {} : { hint: 'Only a pharmacist can change the class.' })}
            onChange={(e) => set('rxClass', e.target.value)}
            options={RX_CLASSES.map((r) => ({ value: r, label: RX_CLASS_LABELS[r] }))}
          />
          <Select
            label="Storage"
            value={f.storageRequirement}
            onChange={(e) => set('storageRequirement', e.target.value)}
            options={STORAGE_REQUIREMENTS.map((s) => ({ value: s, label: STORAGE_REQUIREMENT_LABELS[s] }))}
          />
        </div>
        {f.rxClass !== med.rxClass && (
          <Input
            label="Why is the class changing?"
            hint="Kept in the medicine's classification history, with who changed it and when."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        )}
        <ErrorLine error={save.error} />
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button
            loading={save.isPending}
            disabled={!f.name.trim() || (f.rxClass !== med.rxClass && reason.trim().length < 5)}
            onClick={() =>
              save.mutate(
                {
                  name: f.name.trim(),
                  sku: nul(f.sku),
                  genericName: nul(f.genericName),
                  brandName: nul(f.brandName),
                  strength: nul(f.strength),
                  dosageForm: nul(f.dosageForm),
                  manufacturer: nul(f.manufacturer),
                  registrationNumber: nul(f.registrationNumber),
                  ...(f.rxClass !== med.rxClass ? { rxClass: f.rxClass, classificationReason: reason.trim() } : {}),
                  storageRequirement: f.storageRequirement,
                },
                { onSuccess: onClose },
              )
            }
          >
            Save
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function UnitModal({ productId, baseName, unit, onClose }: { productId: string; baseName: string; unit: Partial<UnitView>; onClose: () => void }): ReactNode {
  const save = usePharmacyWrite<Record<string, unknown>>('put', `/medicines/${productId}/units`);
  const [name, setName] = useState(unit.name ?? '');
  const [size, setSize] = useState(String(unit.baseQuantity ?? ''));
  const [barcode, setBarcode] = useState(unit.barcode ?? '');
  const [price, setPrice] = useState(unit.price ?? '');
  const [sellable, setSellable] = useState(unit.sellable ?? true);
  const sizeOk = unit.isBase ? size === '1' : isWhole(size, 2);
  return (
    <Modal open onClose={onClose} title={unit.id ? `Edit ${unit.name}` : 'Add unit'}>
      <div className="space-y-3">
        <Input label="Unit name" value={name} onChange={(e) => setName(e.target.value)} />
        <Input
          label={`${baseName}s in one`}
          inputMode="numeric"
          value={size}
          disabled={unit.isBase}
          error={size && !sizeOk ? 'A whole number of 2 or more' : undefined}
          hint={unit.id ? 'Refused once this unit has been sold or received.' : undefined}
          onChange={(e) => setSize(e.target.value)}
        />
        <Input label="Barcode" value={barcode} onChange={(e) => setBarcode(e.target.value)} />
        <Input label="Price" inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} />
        <Checkbox label="Sold in this unit" checked={sellable} onChange={setSellable} />
        <ErrorLine error={save.error} />
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button
            disabled={!name.trim() || !sizeOk}
            loading={save.isPending}
            onClick={() =>
              save.mutate(
                {
                  ...(unit.id ? { id: unit.id } : {}),
                  name: name.trim(),
                  baseQuantity: Number(size),
                  barcode: barcode.trim() || null,
                  price: price.trim() || null,
                  sellable,
                  purchasable: unit.purchasable ?? true,
                },
                { onSuccess: onClose },
              )
            }
          >
            Save
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function ShelfModal({
  productId,
  branchId,
  current,
  onClose,
}: {
  productId: string;
  branchId: string;
  current: { shelfId: string | null; reorderLevel: number | null } | null;
  onClose: () => void;
}): ReactNode {
  const shelves = usePharmacy<ListOf<{ id: string; code: string; name: string | null }>>('/shelves', { branchId });
  const save = usePharmacyWrite<Record<string, unknown>>('put', `/medicines/${productId}/locations`);
  const [shelfId, setShelfId] = useState(current?.shelfId ?? '');
  const [reorder, setReorder] = useState(current?.reorderLevel === null || current?.reorderLevel === undefined ? '' : String(current.reorderLevel));
  const reorderOk = reorder === '' || isWhole(reorder, 0);
  return (
    <Modal open onClose={onClose} title="Shelf and reorder level">
      <div className="space-y-3">
        <Select
          label="Shelf or bin"
          value={shelfId}
          placeholder="No shelf"
          onChange={(e) => setShelfId(e.target.value)}
          options={(shelves.data?.data ?? []).map((s) => ({ value: s.id, label: s.name ? `${s.code} — ${s.name}` : s.code }))}
          hint="Shelves are set up in Pharmacy settings."
        />
        <Input label="Reorder at (base units)" inputMode="numeric" value={reorder} error={reorderOk ? undefined : 'A whole number'} onChange={(e) => setReorder(e.target.value)} />
        <ErrorLine error={save.error} />
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button
            disabled={!reorderOk}
            loading={save.isPending}
            onClick={() =>
              save.mutate({ branchId, shelfId: shelfId || null, reorderLevel: reorder === '' ? null : Number(reorder) }, { onSuccess: onClose })
            }
          >
            Save
          </Button>
        </div>
      </div>
    </Modal>
  );
}

interface ClassificationChange {
  at: string;
  by: string | null;
  summary: string;
  before: { rxClass?: string; isMedicine?: boolean } | null;
  after: { rxClass?: string; isMedicine?: boolean } | null;
}

/** When this item was classified, and every change since: who, when, why. */
function ClassificationHistory({ productId }: { productId: string }): ReactNode {
  const { business } = useAuth();
  const history = usePharmacy<ListOf<ClassificationChange>>(`/medicines/${productId}/classification`);
  const rows = history.data?.data ?? [];
  if (rows.length === 0) return null;
  const label = (c: ClassificationChange['after']) =>
    !c ? '—' : c.isMedicine === false ? 'Not a medicine' : c.rxClass ? (RX_CLASS_LABELS[c.rxClass as keyof typeof RX_CLASS_LABELS] ?? c.rxClass) : '—';
  return (
    <Card>
      <CardHeader title="Classification history" subtitle="Who decided what this item legally is, and why" />
      <ul className="mt-3 divide-y divide-slate-100 text-sm">
        {rows.map((r, i) => (
          <li key={i} className="py-2">
            <p className="text-slate-900">
              {i === 0 && !r.before ? 'Added as ' : `${label(r.before)} → `}
              <span className="font-medium">{label(r.after)}</span>
            </p>
            <p className="text-xs text-slate-500">
              {new Date(r.at).toLocaleString(undefined, { timeZone: business?.timezone ?? 'UTC' })} · {r.by ?? 'Unknown'} · {r.summary}
            </p>
          </li>
        ))}
      </ul>
    </Card>
  );
}

/**
 * Correct a batch record entered wrongly: number, expiry, manufacturing date
 * or cost. A stock approver's decision with a reason; quantities are corrected
 * by an adjustment instead. The server refuses a cost once any of the batch
 * has left at it, and a medicine's batch unless the approver's registration
 * is on file.
 */
function CorrectBatchModal({ batch, onClose }: { batch: BatchRow; onClose: () => void }): ReactNode {
  const correct = usePharmacyWrite<Record<string, unknown>>('post', `/batches/${batch.batchId}/correct`);
  const [f, setF] = useState({
    batchNumber: batch.batchNumber,
    expiryDate: batch.expiryDate,
    manufacturedOn: batch.manufacturedOn ?? '',
    unitCost: String(Number(batch.unitCost)),
    reason: '',
  });
  const changes: Record<string, unknown> = {};
  if (f.batchNumber.trim() && f.batchNumber.trim() !== batch.batchNumber) changes.batchNumber = f.batchNumber.trim();
  if (f.expiryDate && f.expiryDate !== batch.expiryDate) changes.expiryDate = f.expiryDate;
  if ((f.manufacturedOn || null) !== (batch.manufacturedOn ?? null)) changes.manufacturedOn = f.manufacturedOn || null;
  if (f.unitCost.trim() && Number(f.unitCost) !== Number(batch.unitCost)) changes.unitCost = f.unitCost.trim();
  const ready = Object.keys(changes).length > 0 && f.reason.trim().length >= 5;
  return (
    <Modal open onClose={onClose} title={`Correct batch ${batch.batchNumber}`}>
      <div className="space-y-3">
        <p className="text-sm text-slate-600">
          For a record entered wrongly. To change how many there are, record an adjustment instead. Your name and reason are kept with
          the change.
        </p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Input label="Batch number" value={f.batchNumber} onChange={(e) => setF({ ...f, batchNumber: e.target.value })} />
          <Input label="Expiry date" type="date" value={f.expiryDate} onChange={(e) => setF({ ...f, expiryDate: e.target.value })} />
          <Input label="Made on" type="date" value={f.manufacturedOn} onChange={(e) => setF({ ...f, manufacturedOn: e.target.value })} />
          <Input label="Cost per base unit" inputMode="decimal" value={f.unitCost} onChange={(e) => setF({ ...f, unitCost: e.target.value })} />
        </div>
        <Input label="Why is it being corrected?" required value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} placeholder="e.g. Expiry misread from the pack" />
        <ErrorLine error={correct.error} />
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button disabled={!ready} loading={correct.isPending} onClick={() => correct.mutate({ ...changes, reason: f.reason.trim() }, { onSuccess: onClose })}>
            Save correction
          </Button>
        </div>
      </div>
    </Modal>
  );
}
